import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const IMPERSONATION_KEY = "cdi.impersonation.admin";
const TOKEN_KEY = "cdi.auth.token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

function fmtDuration(startIso) {
  if (!startIso) return "";
  try {
    const startMs = new Date(startIso).getTime();
    if (isNaN(startMs)) return "";
    const secs = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  } catch { return ""; }
}

// Persistent red bar shown at the top of every page while an admin is
// impersonating another user. Reads `cdi.impersonation.admin` from
// localStorage — set by AdminDashboard when the admin clicks Impersonate,
// cleared here when they click "Return to admin".
export default function ImpersonationBanner() {
  const [state, setState] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem(IMPERSONATION_KEY);
        setState(raw ? JSON.parse(raw) : null);
      } catch { setState(null); }
    };
    read();
    window.addEventListener("storage", read);
    return () => window.removeEventListener("storage", read);
  }, []);

  // Tick every second so the elapsed timer stays live without React churn on
  // the whole page. Only runs while the banner is actually visible.
  useEffect(() => {
    if (!state?.admin_token) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [state?.admin_token]);

  if (!state || !state.admin_token) return null;

  const elapsed = fmtDuration(state.started_at);
  // Reference `now` so ESLint knows this drives the elapsed re-render.
  void now;

  const returnToAdmin = async () => {
    // Compute duration before we lose the started_at.
    let durationSeconds = 0;
    try {
      const startMs = new Date(state.started_at).getTime();
      if (!isNaN(startMs)) durationSeconds = Math.max(0, Math.floor((Date.now() - startMs) / 1000));
    } catch { /* noop */ }
    // Snapshot fields for the audit call before we clear the flag.
    const adminToken = state.admin_token;
    const targetEmail = state.target_email;
    // Swap the admin token back FIRST so the audit call authenticates as
    // the original admin (require_admin_session on the endpoint).
    try {
      localStorage.setItem(TOKEN_KEY, adminToken);
      localStorage.removeItem(IMPERSONATION_KEY);
    } catch { /* private mode */ }
    // Best-effort audit — do not block navigation on network failure.
    try {
      await axios.post(
        `${API}/impersonation/return`,
        { target_email: targetEmail, duration_seconds: durationSeconds },
        { withCredentials: true, headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {} },
      );
    } catch { /* noop */ }
    toast.success("Returned to admin session");
    setTimeout(() => { window.location.href = "/admin"; }, 400);
  };

  return (
    <div
      data-testid="impersonation-banner"
      className="w-full flex items-center justify-center gap-4 py-2 px-4 text-sm flex-wrap"
      style={{ background: "#3a1414", color: "#ff9a9a", borderBottom: "1px solid #6a2020", fontWeight: 600 }}
    >
      <span>
        Impersonating <span data-testid="impersonation-target-email" style={{ color: "#ffd7d7" }}>{state.target_email}</span>
        {state.admin_email ? <> · original admin <span style={{ color: "#ffd7d7" }}>{state.admin_email}</span></> : null}
      </span>
      {elapsed ? (
        <span
          data-testid="impersonation-timer"
          className="px-2 py-1 rounded text-xs tabular-nums"
          style={{ background: "#6a2020", color: "#ffe6e6", border: "1px solid #a04040" }}
          title={`Started ${state.started_at}`}
        >
          {elapsed}
        </span>
      ) : null}
      <button
        data-testid="impersonation-return-btn"
        onClick={returnToAdmin}
        className="px-3 py-1 rounded-md text-xs"
        style={{ background: "#6a2020", color: "#ffe6e6", border: "1px solid #a04040" }}
      >
        Return to admin
      </button>
    </div>
  );
}
